import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

// === helpers ===
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function stripOuterParens(s) {
  let t = (s || "").trim();
  if (t.startsWith("(") && t.endsWith(")")) t = t.slice(1, -1).trim();
  return t;
}

// Akceptujemy przemienność dodawania: x+7 == 7+x
// Nie akceptujemy "przemienności odejmowania": x-7 != 7-x
function normalizeSum(expr) {
  return String(expr ?? "")
    .replace(/−/g, "-")
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .toLowerCase()
    .replace(/\\dfrac\{([^}]+)\}\{([^}]+)\}/g, "$1/$2")
    .replace(/-/g, "+-")
    .split("+")
    .filter(Boolean)
    .sort()
    .join("+");
}

function splitFraction(raw) {
  const s = String(raw ?? "").trim();

  // akceptuj zapis \dfrac{...}{...}
  const dfrac = s.match(/\\dfrac\{(.+)\}\{(.+)\}/);
  if (dfrac) return [dfrac[1], dfrac[2]];

  // bierzemy pierwszy slash (w praktyce uczniowie tu wpisują pojedynczy)
  const idx = s.indexOf("/");
  if (idx === -1) return [null, null];

  return [s.slice(0, idx), s.slice(idx + 1)];
}

// === GENERATE TASK ===
export function generateTask() {
  // a w {2..9} (żeby było "maturalnie" i bez trywialnych przypadków)
  const a = pick([2, 3, 4, 5, 6, 7, 8, 9]);

  // (x+a)^2 rozpisane jako trójmian
  const denomExpandedTex = `x^2+${2 * a}x+${a * a}`;

  // x^2 - a^2
  const numeratorTex = `x^2-${a * a}`;

  // wynik: (x-a)/(x+a)
  const expectedNum = `x-${a}`;
  const expectedDen = `x+${a}`;

  // dziedzina: (x+a)^2 ≠ 0 => x ≠ -a
  const domainTex = `x\\neq -${a}`;

  return {
    type: "DiffSquaresOverSquareExpandedTask",
    data: {
      a,
      numeratorTex,
      denomExpandedTex,
      expectedNum,
      expectedDen,
      domainTex,
    },
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  const { numeratorTex, denomExpandedTex, domainTex } = data;

  return (
    <div className="space-y-2">
      <p>
        Uprość wyrażenie. Załóż, że <InlineMath math={domainTex} />.
      </p>
      <BlockMath math={`\\frac{${numeratorTex}}{${denomExpandedTex}}`} />
    </div>
  );
}

// === RENDER INPUT ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={"np. (x-2)/(x+2)"}
      showPreview
    />
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string") {
    return {
      isCorrect: false,
      expected: `(${data.expectedNum})/(${data.expectedDen})`,
    };
  }

  const [rawNum, rawDen] = splitFraction(userAnswer);
  if (!rawNum || !rawDen) {
    return {
      isCorrect: false,
      expected: `(${data.expectedNum})/(${data.expectedDen})`,
    };
  }

  const num = stripOuterParens(rawNum);
  const den = stripOuterParens(rawDen);

  const ok =
    normalizeSum(num) === normalizeSum(data.expectedNum) &&
    normalizeSum(den) === normalizeSum(data.expectedDen);

  return {
    isCorrect: ok,
    expected: `(${data.expectedNum})/(${data.expectedDen})`,
  };
}

// === RENDER ANSWER ===
export function renderAnswer({ data }) {
  return <InlineMath math={`\\frac{${data.expectedNum}}{${data.expectedDen}}`} />;
}

// === EXPLANATION ===
export function renderExplanation({ data }) {
  const { a, numeratorTex, denomExpandedTex, expectedNum, expectedDen, domainTex } =
    data;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Zwiń mianownik do wzoru skróconego mnożenia:
      </p>
      <BlockMath math={`${denomExpandedTex}=(x+${a})^2`} />

      <p>
        <strong>Krok 2:</strong> Rozłóż licznik jako różnicę kwadratów:
      </p>
      <BlockMath math={`${numeratorTex}=x^2-${a * a}=(x-${a})(x+${a})`} />

      <p>
        <strong>Krok 3:</strong> Skróć wspólny czynnik <InlineMath math={`(x+${a})`} />:
      </p>
      <BlockMath
        math={`\\frac{(x-${a})(x+${a})}{(x+${a})^2}
        =\\frac{x-${a}}{x+${a}}
        =\\frac{${expectedNum}}{${expectedDen}}`}
      />

      <p className="text-sm opacity-90">
        Warunek <InlineMath math={domainTex} /> wynika z tego, że mianownik{" "}
        <InlineMath math={`(x+${a})^2`} /> nie może być równy zero.
      </p>
    </div>
  );
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(...args) {
  const ua = args.length === 1 ? args[0] : args[1];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}
